import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../../../../store/models'
import { User } from '../../../../models/User'
import { Room, RoomSession } from '../../../../models/Room'
import { baseQueryWithTokenRefresh } from '../../../../common/baseQuery'
import { SessionFilter, SessionSort } from '../../../../components/SessionFilters/model'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!

export interface GetUserRoomSessionsRequest extends BasePageRequest {
  userId?: string
  startedAfter?: number
  startedBefore?: number
  participantId?: string
}

export interface GetRoomSessionsByFilterRequest extends BasePageRequest {
  filter?: SessionFilter
  filterId?: string
  sort?: SessionSort
}

export interface GetRoomRequest extends BaseRequest {
  roomId: string
}

export interface DeleteRoomSessionRequest extends BaseRequest {
  roomId: string
  sessionId: string
}

export interface LiveStatus {
  hasLiveSession: boolean
}

export const faApi = createApi({
  reducerPath: 'faApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Session'],
  endpoints: (builder) => ({
    getRoom: builder.query<Room, GetRoomRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}`,
        }
      },
      transformResponse: (response: BaseResponse<Room>) => {
        return response.data
      },
      providesTags: ['Session'],
    }),
    getUserRoomSessions: builder.query<BasePageResponse<RoomSession[]>, GetUserRoomSessionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/`,
          params: {
            userId: request.userId,
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            startedAfter: request.startedAfter,
            startedBefore: request.startedBefore,
            participantId: request.participantId,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getRoomSessionsForUser: builder.query<BasePageResponse<RoomSession[]>, GetUserRoomSessionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/list-for-user`,
          method: 'POST',
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
          body: {
            userId: request.userId,
            startedAfter: request.startedAfter,
            startedBefore: request.startedBefore,
            participantId: request.participantId,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getRoomSessionsForFilter: builder.query<BasePageResponse<RoomSession[]>, GetRoomSessionsByFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/filter`,
          method: 'POST',
          body: {
            filter: request.filter,
            filterId: request.filterId,
            sort: request.sort,
          },
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getLiveStatus: builder.query<BaseResponse<LiveStatus>, BaseRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/live-status`,
          method: 'GET',
        }
      },
    }),
    deleteRoomSession: builder.mutation<BaseResponse<User>, DeleteRoomSessionRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Session'],
    }),
    getPhoneCandidates: builder.query<BasePageResponse<RoomSession[]>, BasePageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/phone-candidates`,
          method: 'GET',
        }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetRoomQuery,
  useLazyGetUserRoomSessionsQuery,
  useLazyGetRoomSessionsForUserQuery,
  useLazyGetRoomSessionsForFilterQuery,
  useGetRoomSessionsForFilterQuery,
  useDeleteRoomSessionMutation,
  useGetLiveStatusQuery,
  useGetPhoneCandidatesQuery,
} = faApi
